import Link from 'next/link';
import styled from 'styled-components';

import { Typography } from '@/components/ui/Typography/Typography.styles';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { ParticipantEnum } from '@/constants/common';
import { flexCenterAligned, flexColumn } from '@/styles/common';

export const Wrapper = styled.div`
  ${flexColumn};
  @media (max-width: ${BREAKPOINTS.mobile}) {
    row-gap: 0px;
  }
`;

export const Section = styled.div<{ $isLast: boolean }>`
  position: relative;
  transition: background-color 0.3s linear;
  padding: 0px 80px;
  margin-bottom: ${({ $isLast }) => ($isLast ? '70px' : '50px')};

  ${flexColumn};

  @media (max-width: ${BREAKPOINTS.desktop}) {
    padding-inline: 40px;
    margin-bottom: 30px;
  }
  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding-inline: 16px;
  }
`;

export const MainContentWrapper = styled.div<{
  $isResumes?: boolean;
}>`
  background-color: ${({ $isResumes }) =>
    $isResumes ? 'var(--system-brand)' : 'var(--bg-card)'};
  padding-inline: 80px;
  padding-top: 100px;
  padding-bottom: 100px;
  overflow: hidden;
  position: relative;

  @media (max-width: ${BREAKPOINTS.desktop}) {
    padding-inline: var(--px-md);
  }

  @media (max-width: ${BREAKPOINTS.tablet}) {
    padding-inline: var(--px-sm);
    padding-top: 40px;
    padding-bottom: 70px;
  }
`;

export const SectionHeader = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
`;

export const SectionContent = styled.div`
  position: relative;
  margin: auto;
  ${flexColumn};
  align-items: center;
  gap: 40px;
  z-index: 1;
`;

export const SearchBlockTitle = styled(Typography).attrs({ variant: 'h0' })<{
  $isResumes?: boolean;
}>`
  font-size: 36px;
  max-width: 594px;
  width: 100%;
  text-align: center;
  margin-bottom: 20px;
  color: ${({ $isResumes }) =>
    $isResumes ? 'var(--textAndLabels-dark)' : 'var(--textAndLabels-main)'};

  @media (max-width: ${BREAKPOINTS.tablet}) {
    text-align: left;
    margin-bottom: 0px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin-bottom: 0;
    font-size: 30px;
  }
`;

export const SearchBlockWrapper = styled.div`
  ${flexCenterAligned};
  flex-direction: column;
  row-gap: 40px;
  font-size: 36px;
  text-align: center;
  width: 100%;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    align-items: baseline;
  }

  @media (min-width: ${BREAKPOINTS.tablet}) {
    row-gap: 30px;
    max-width: 800px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    row-gap: 30px;
  }
`;

export const BackgroundIconWrapper = styled.div<{
  $participant: ParticipantEnum;
}>`
  overflow: hidden;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  & svg {
    transition: color 0.3s linear;
    margin-right: -5%;
    margin-top: -24px;
    color: ${({ $participant }) =>
      `var(--dashboard-${$participant.toLowerCase()}-icon)`};
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    bottom: -100px;
    left: 120px;
    right: -60px;
  }
`;

export const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--textAndLabels-main);

  & > svg * {
    fill: var(--textAndLabels-main);
  }

  @media (max-width: ${BREAKPOINTS.tablet}) {
    justify-content: space-between;
    width: 100%;
  }
`;

export const ContentWrapper = styled.div`
  background: var(--bg-main);

  z-index: 3;
  padding-top: 30px;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    border-radius: 28px 28px 0 0;
    margin-top: -30px;
  }

  @media (max-width: ${BREAKPOINTS.tablet}) {
    margin-bottom: 0px;
  }
`;

export const StatisticLink = styled(Link)<{
  $isDark?: boolean;
  $isResumes?: boolean;
}>`
  ${flexCenterAligned};
  gap: 3px;
  color: ${({ $isResumes }) =>
    $isResumes ? 'var(--textAndLabels-dark)' : 'var(--textAndLabels-main)'};
  position: relative;

  &:after {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background-color: ${({ $isResumes }) =>
      $isResumes ? 'var(--textAndLabels-dark)' : 'var(--textAndLabels-main)'};
    position: absolute;
    bottom: 0;
    left: 0;
  }
`;

export const StatisticTitle = styled(Typography).attrs({ variant: 'body1' })`
  ${flexCenterAligned};

  @media (max-width: ${BREAKPOINTS.tablet}) {
    justify-content: flex-start;
  }
`;

export const HightlightText = styled.span<{
  $isResumes?: boolean;
  $isDark?: boolean;
}>`
  color: ${({ $isResumes }) => {
    return $isResumes ? 'var(--textAndLabels-dark)' : 'var(--system-yellow)';
  }};
`;

export const TypographyCenter = styled(Typography)`
  text-align: center;
`;

export const NoDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding-block: 100px;
  padding-inline: 80px;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    padding-inline: 40px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding-inline: 16px;
  }
`;
